import React, { useState } from 'react';
import { Search, Palette, Code2, Rocket, HeartHandshake, ChevronDown } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: <Search size={20} />,
    title: 'Discovery & Strategy',
    duration: 'Week 1–2',
    tag: 'Foundation',
    tagColor: '#8B5CF6',
    color: 'rgba(139,92,246,0.12)',
    borderColor: 'rgba(139,92,246,0.3)',
    desc: 'We spend real time understanding your business — not just the feature list. This means stakeholder interviews, competitor analysis, and a technical feasibility review.',
    deliverables: ['Product requirements doc', 'Technical architecture plan', 'Risk & dependency map', 'Project timeline + milestones'],
    honest: 'Most projects fail because of unclear requirements, not bad code. We refuse to skip this.',
  },
  {
    number: '02',
    icon: <Palette size={20} />,
    title: 'Design & Architecture',
    duration: 'Week 2–4',
    tag: 'Blueprint',
    tagColor: '#F59E0B',
    color: 'rgba(245,158,11,0.08)',
    borderColor: 'rgba(245,158,11,0.25)',
    desc: 'UI/UX design and system architecture happen in parallel. You review and approve before a single line of production code is written.',
    deliverables: ['High-fidelity Figma mockups', 'System architecture diagrams', 'Database schema design', 'API contract definitions'],
    honest: 'You own every design file. If we part ways after this phase, you walk away with a complete blueprint.',
  },
  {
    number: '03',
    icon: <Code2 size={20} />,
    title: 'Development',
    duration: 'Week 4–16',
    tag: 'Build',
    tagColor: '#258ACB',
    color: 'rgba(37,138,203,0.08)',
    borderColor: 'rgba(37,138,203,0.25)',
    desc: '2-week sprints with a working demo at the end of each one. You see real progress, not status update emails. Every sprint ships testable code to a staging environment.',
    deliverables: ['Weekly sprint demos', 'Staging environment access', 'Code review & documentation', 'Automated test coverage'],
    honest: 'Timeline depends on scope. A lean MVP is 4–6 weeks. A full platform is 12–16. We\'ll tell you exactly which you need.',
  },
  {
    number: '04',
    icon: <Rocket size={20} />,
    title: 'Launch & Deployment',
    duration: 'Week 16–17',
    tag: 'Go Live',
    tagColor: '#10B981',
    color: 'rgba(16,185,129,0.08)',
    borderColor: 'rgba(16,185,129,0.25)',
    desc: 'Production deployment with zero downtime. We set up monitoring, alerts, and run load tests before flipping the switch. You watch the dashboard go green in real time.',
    deliverables: ['Production deployment', 'Monitoring & alerting setup', 'Performance & load testing', 'Handover documentation'],
    honest: 'We don\'t "launch and leave." The first 48 hours post-launch we\'re on-call for anything that comes up.',
  },
  {
    number: '05',
    icon: <HeartHandshake size={20} />,
    title: 'Support & Growth',
    duration: 'Ongoing',
    tag: 'Long-term',
    tagColor: '#EC4899',
    color: 'rgba(236,72,153,0.07)',
    borderColor: 'rgba(236,72,153,0.22)',
    desc: 'Most of our clients have been with us for 2+ years. We become an extension of your team — handling maintenance, iterating on features, and scaling infrastructure as you grow.',
    deliverables: ['Monthly retainer options', 'Feature development sprints', 'Infrastructure scaling', 'Performance optimization'],
    honest: 'No lock-in contracts. We earn your continued business by doing good work, not by making it painful to leave.',
  },
];

export default function Process() {
  const [open, setOpen] = useState(0);

  return (
    <section id="process" style={{ padding: '7rem 0', background: 'var(--bg-secondary)', position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', width: '600px', height: '600px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37,138,203,0.06) 0%, transparent 70%)',
        top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        filter: 'blur(60px)', pointerEvents: 'none',
      }} />

      <div className="container">
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'end', marginBottom: '4rem' }} className="process-header">
          <div>
            <div className="section-label"><span>◆</span> How We Work</div>
            <h2 className="section-heading">
              A process built on<br /><span className="gradient-text">transparency</span>
            </h2>
          </div>
          <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            No surprises, no black boxes. You know exactly what's happening, when it's happening, and why — at every phase. Here's what working with Nuage actually looks like.
          </p>
        </div>

        {/* Timeline bar */}
        <div style={{
          display: 'flex', alignItems: 'stretch',
          background: 'rgba(37,138,203,0.04)', border: '1px solid var(--border)',
          borderRadius: '12px', overflow: 'hidden', marginBottom: '2rem',
        }} className="timeline-bar">
          {steps.map((s, i) => (
            <button key={i} onClick={() => setOpen(i)} type="button" style={{
              flex: s.number === '03' ? 2 : 1,
              padding: '0.85rem 1rem',
              background: open === i ? s.color : 'transparent',
              borderRight: i < steps.length - 1 ? '1px solid var(--border)' : 'none',
              border: 'none',
              borderBottom: open === i ? `2px solid ${s.tagColor}` : '2px solid transparent',
              cursor: 'pointer', transition: 'all 0.25s', textAlign: 'left',
            }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', color: open === i ? s.tagColor : 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                {s.duration}
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, fontFamily: 'var(--font-display)', color: open === i ? 'var(--text-primary)' : 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {s.title}
              </div>
            </button>
          ))}
        </div>

        {/* Accordion steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {steps.map((step, i) => {
            const isOpen = open === i;
            return (
              <div key={i} style={{
                borderRadius: '14px', overflow: 'hidden',
                border: isOpen ? `1px solid ${step.borderColor}` : '1px solid var(--border)',
                background: isOpen ? step.color : 'rgba(37,138,203,0.02)',
                transition: 'all 0.3s ease',
              }}>
                {/* Row header */}
                <button onClick={() => setOpen(isOpen ? -1 : i)} type="button" style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  gap: '1.25rem', padding: '1.25rem 1.5rem',
                  background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}>
                  {/* Step number */}
                  <div style={{
                    width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                    background: isOpen ? step.color : 'rgba(37,138,203,0.06)',
                    border: `1px solid ${isOpen ? step.borderColor : 'var(--border)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: isOpen ? step.tagColor : 'var(--text-muted)',
                    transition: 'all 0.3s',
                  }}>
                    {step.icon}
                  </div>

                  {/* Title group */}
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <span style={{
                      fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '0.68rem',
                      color: isOpen ? step.tagColor : 'var(--text-muted)',
                      letterSpacing: '0.12em', minWidth: '24px',
                    }}>{step.number}</span>
                    <span style={{
                      fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700,
                      color: 'var(--text-primary)',
                    }}>{step.title}</span>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em',
                      color: step.tagColor,
                      background: `${step.tagColor}18`,
                      border: `1px solid ${step.tagColor}40`,
                      borderRadius: '100px', padding: '0.18rem 0.65rem',
                    }}>{step.tag}</span>
                  </div>

                  {/* Duration badge */}
                  <span style={{
                    fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)',
                    borderRadius: '6px', padding: '0.25rem 0.7rem', flexShrink: 0,
                  }}>{step.duration}</span>

                  <ChevronDown size={16} style={{
                    color: 'var(--text-muted)', flexShrink: 0,
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.3s',
                  }} />
                </button>

                {/* Expanded content */}
                {isOpen && (
                  <div style={{
                    padding: '0 1.5rem 1.5rem',
                    display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '1.5rem', animation: 'expandIn 0.3s ease',
                  }} className="process-expanded">
                    {/* Left: description + honest note */}
                    <div>
                      <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.25rem' }}>
                        {step.desc}
                      </p>
                      {/* Honest note */}
                      <div style={{
                        display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
                        background: `${step.tagColor}0e`,
                        border: `1px solid ${step.tagColor}28`,
                        borderRadius: '10px', padding: '0.9rem 1rem',
                      }}>
                        <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: '0.05rem' }}>💬</span>
                        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.65, fontStyle: 'italic' }}>
                          {step.honest}
                        </p>
                      </div>
                    </div>

                    {/* Right: deliverables */}
                    <div>
                      <div style={{
                        fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em',
                        textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.85rem',
                      }}>
                        What you get
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                        {step.deliverables.map((d, j) => (
                          <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                            <div style={{
                              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                              background: `${step.tagColor}18`,
                              border: `1px solid ${step.tagColor}40`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <div style={{ width: 6, height: 6, borderRadius: '50%', background: step.tagColor }} />
                            </div>
                            <span style={{ fontSize: '0.88rem', color: 'var(--text-primary)', fontWeight: 500 }}>{d}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '1.5rem',
          marginTop: '3rem', padding: '1.75rem 2rem',
          background: 'rgba(37,138,203,0.06)', border: '1px solid rgba(37,138,203,0.18)',
          borderRadius: '14px',
        }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' }}>
              Ready to start your project?
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Discovery call is free. No commitment, no sales pressure.
            </div>
          </div>
          <a href="#contact" className="btn-primary" style={{ whiteSpace: 'nowrap' }}>
            Start with Discovery →
          </a>
        </div>
      </div>

      <style>{`
        @keyframes expandIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:none} }
        @media (max-width: 860px) {
          .process-header { grid-template-columns: 1fr !important; gap: 1.5rem !important; }
          .timeline-bar { display: none !important; }
          .process-expanded { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}